import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { BottomNavigation } from '../components/BottomNavigation';
import { SideNavigation } from '../components/SideNavigation';
import { ChecklistWidget } from '../components/ChecklistWidget';
import { SellerHeader } from '../components/SellerHeader';
import { BackButton } from '../components/ui';

import { useAuth } from '../contexts/AuthContext';
import { useOnboardingStatus } from '../hooks/useOnboardingStatus';
import { useQuickActions } from '../contexts/QuickActionsContext';
import { getProductsBySeller } from '../utils/productApi';
import { getSellerOrdersWithItems, getGroupStatus as getCentralizedGroupStatus } from '../utils/multiVendorOrderApi';
import { getSellerFinancials } from '../lib/financialService';
import { getStoreRatingSummary, getProductReviews, refreshStoreRating } from '../lib/reviewsService';
import { getSellerAnalytics } from '../lib/analyticsService';
import { calculateInventoryAlerts } from '../utils/productApi';
import { COLORS, GRADIENT_UTILS } from '../lib/styleGuide';
import { 
  ArrowUp,
  ArrowDown,
  Plus,
  Buy,
  Discount,
  Wallet,
  Star,
  Calendar,
  Location,
  Notification,
  User,
  MoreCircle,
  Show
} from 'react-iconly';
import { collection, query, where, getDocs, updateDoc, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { getSellerMerchantOrders } from '../utils/multiVendorOrderApi';
import { updateMerchantOrderStatus } from '../utils/orderStatusApi';

// Style guide colors
const PRIMARY_BROWN = '#C97B53';
const NEUTRAL_BG = '#F9F9F9';
const NEUTRAL_WHITE = '#FFF';
const NEUTRAL_TEXT = '#222';
const ACCENT_GRAY = '#757575';
const SUCCESS_GREEN = '#4CAF50';
const WARNING_ORANGE = '#FF9800';
const ERROR_RED = '#F44336';

const headingFont = {
  fontFamily: 'Sora, sans-serif',
  fontWeight: 700,
};
const bodyFont = {
  fontFamily: 'Montserrat, sans-serif',
  fontWeight: 500,
};

// Dummy dashboard data
const dummyMetrics = {
  totalRevenue: 2847.50,
  totalOrders: 156,
  averageOrderValue: 18.25,
  conversionRate: 12.8,
  revenueChange: 8.2,
  ordersChange: 5.4,
  aovChange: -2.1,
  conversionChange: 1.2
};

// Dummy revenue trend data (last 7 days)
const dummyRevenueTrend = [
  { date: '2024-01-08', revenue: 320 },
  { date: '2024-01-09', revenue: 450 },
  { date: '2024-01-10', revenue: 380 },
  { date: '2024-01-11', revenue: 520 },
  { date: '2024-01-12', revenue: 480 },
  { date: '2024-01-13', revenue: 610 },
  { date: '2024-01-14', revenue: 540 }
];

// Dummy orders data
const dummyOrders = [
  {
    id: 'ORD-2024-001',
    customerName: 'Sarah M.',
    date: '2024-01-14T14:30:00Z',
    total: 45.97,
    status: 'pending',
    items: 2
  },
  {
    id: 'ORD-2024-002',
    customerName: 'Mike T.',
    date: '2024-01-14T13:15:00Z',
    total: 32.98,
    status: 'preparing',
    items: 1
  },
  {
    id: 'ORD-2024-003',
    customerName: 'Jennifer L.',
    date: '2024-01-14T12:45:00Z',
    total: 18.99,
    status: 'ready',
    items: 1
  },
  {
    id: 'ORD-2024-004',
    customerName: 'David K.',
    date: '2024-01-14T11:20:00Z',
    total: 67.95,
    status: 'delivered',
    items: 3
  },
  {
    id: 'ORD-2024-005',
    customerName: 'Emma R.',
    date: '2024-01-14T10:30:00Z',
    total: 25.98,
    status: 'delivered',
    items: 2
  }
];

// Status tag logic from Orders.js
const STATUS_WORKFLOW = {
  placed: { tag: 'NEW', key: 'new', action: 'Accept Order', next: 'accepted' },
  accepted: { tag: 'ACCEPTED', key: 'accepted', action: 'Start Preparing', next: 'in-progress' },
  'in-progress-pickup': { tag: 'PREPARING', key: 'preparing', action: 'Mark as Ready', next: 'ready' },
  'in-progress-delivery': { tag: 'PREPARING', key: 'preparing', action: 'Start Delivery', next: 'out-for-delivery' },
  'ready': { tag: 'READY', key: 'ready', action: 'Mark as Complete', next: 'completed' },
  'out-for-delivery': { tag: 'DELIVERING', key: 'delivering', action: 'Mark as Complete', next: 'completed' },
  'completed': { tag: 'COMPLETED', key: 'completed', action: null, next: null },
  'canceled': { tag: 'CANCELED', key: 'canceled', action: null, next: null },
};

const STATUS_TAG_STYLES = {
  new: { bg: '#F3E8FF', text: '#6D28D9' },
  accepted: { bg: '#E3F2FD', text: '#1565C0' },
  preparing: { bg: '#FFFDE7', text: '#F9A825' },
  ready: { bg: '#FFFDE7', text: '#F9A825' },
  delivering: { bg: '#FFF3E0', text: '#EF6C00' },
  completed: { bg: '#E8F5E9', text: '#2E7D32' },
  canceled: { bg: '#FFEBEE', text: '#C62828' },
};

function getWorkflowKey(order) {
  switch (order.status) {
    case 'placed':
      return 'placed';
    case 'confirmed':
      return 'placed';
    case 'accepted':
      return 'accepted';
    case 'processing':
      return order.type === 'delivery' ? 'in-progress-delivery' : 'in-progress-pickup';
    case 'in-progress':
      return order.type === 'delivery' ? 'in-progress-delivery' : 'in-progress-pickup';
    case 'ready':
      return 'ready';
    case 'out-for-delivery':
      return 'out-for-delivery';
    case 'delivered':
    case 'completed':
      return 'completed';
    case 'cancelled':
    case 'canceled':
      return 'canceled';
    default:
      return order.status;
  }
}

export default function SellerHubComponent() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { canAccessSellerHub, canAccessSellerHubDirect, debugSetupFeeStatus, loading: onboardingLoading, fetchStatus } = useOnboardingStatus(); // Added fetchStatus
  

  const [timeRange, setTimeRange] = useState('this-month');
  const [activeAction, setActiveAction] = useState(null);
  const { isQuickActionsOpen } = useQuickActions();

  const [isNavDropdownOpen, setIsNavDropdownOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [activeProductTab, setActiveProductTab] = useState('active');
  const [showCloseShopModal, setShowCloseShopModal] = useState(false);
  const [showFinalConfirm, setShowFinalConfirm] = useState(false);
  const [deleteInput, setDeleteInput] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const [metrics, setMetrics] = useState(dummyMetrics);
  const [revenueTrend, setRevenueTrend] = useState(dummyRevenueTrend);
  const [financialData, setFinancialData] = useState(null);
  const [financialLoading, setFinancialLoading] = useState(true);
  const [ratingsData, setRatingsData] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [canAccess, setCanAccess] = useState(false);
  const [setupFeeCompleted, setSetupFeeCompleted] = useState(false);
  const [storeStatus, setStoreStatus] = useState('inactive');
  const [onboardingCompleted, setOnboardingCompleted] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const hasCalledFetchStatus = useRef(false); // Track if we've already called fetchStatus
  
  // Debug log when metrics change (only in development)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('Metrics state updated:', metrics);
    }
  }, [metrics]);

  // Debug log for store status and checklist visibility
  useEffect(() => {
    console.log('SellerHub: Store status changed to:', storeStatus);
    console.log('SellerHub: Onboarding completed:', onboardingCompleted);
    console.log('SellerHub: ChecklistWidget should show:', !onboardingCompleted);
  }, [storeStatus, onboardingCompleted]);
  
  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Simplified access check - runs once when component mounts
  useEffect(() => {
    console.log('SellerHub: Access check useEffect triggered - currentUser:', !!currentUser, 'canAccess:', canAccess);
    if (currentUser && !canAccess) {
      const checkAccess = async () => {
        try {
          console.log('SellerHub: Starting shop access check for user:', currentUser.uid);
          // First check if user already has a store
          const shopsRef = collection(db, 'shops');
          const q = query(shopsRef, where('sellerId', '==', currentUser?.uid));
          const querySnapshot = await getDocs(q);
          
          console.log('SellerHub: Shop query completed - empty:', querySnapshot.empty, 'docs count:', querySnapshot.docs.length);
          
          if (!querySnapshot.empty) {
            // Get the store data to check its status
            const storeDoc = querySnapshot.docs[0];
            const storeData = storeDoc.data();
            const currentStoreStatus = storeData.status || 'inactive';
            setStoreStatus(currentStoreStatus);
            console.log('SellerHub: Store status set to:', currentStoreStatus);
            
            // Get user document to check onboarding status
            try {
              const userRef = doc(db, 'users', currentUser.uid);
              const userSnap = await getDoc(userRef);
              if (userSnap.exists()) {
                const userData = userSnap.data();
                console.log('SellerHub: User onboarding status:', userData.onboardingCompleted);
                setOnboardingCompleted(userData.onboardingCompleted || false);
                setSetupFeeCompleted(userData.setupFeePaid || false);
              }
            } catch (error) {
              console.error('Error getting user onboarding status:', error);
            }
            setCanAccess(true);
            return;
          }
          
          // If no store exists, check setup fee status
          const hasAccess = await canAccessSellerHubDirect();
          setCanAccess(hasAccess);
          
          if (!hasAccess) {
            navigate('/onboarding');
          }
        } catch (error) {
          console.error('Error checking store access:', error);
          // Set access to true as fallback to prevent infinite loading
          setCanAccess(true);
        }
      };

      checkAccess();
    }
  }, [currentUser, canAccess, canAccessSellerHubDirect, navigate]);

  // Fetch seller's products with memoization
  const fetchProducts = useCallback(async () => {
    if (!currentUser) return;
    
    try {
      setLoading(true);
      const sellerProducts = await getProductsBySeller(currentUser.uid);
      
      console.log('🔍 Raw seller products:', sellerProducts);
      console.log('🔍 Sample product structure:', sellerProducts[0]);
      
      // Transform the data to match the expected format and fetch review data
      const transformedProducts = await Promise.all(
        sellerProducts.map(async (product) => {
          try {
            const reviews = await getProductReviews(product.id);
            const avgRating = reviews.length > 0 
              ? reviews.reduce((sum, review) => sum + review.rating, 0) / reviews.length 
              : 0;
            
            return {
              id: product.id,
              name: product.name,
              image: product.imageUrl || 'https://images.unsplash.com/photo-1621996346565-e3dbc353d2e5?w=400&h=300&fit=crop', // fallback image
              price: product.unitPrice || product.price || 0, // Use unitPrice first, then fallback to price
              stock: product.stock || 0,
              status: product.status !== undefined ? product.status : true,
              published: product.published !== undefined ? product.published : true, // Add published field
              rating: avgRating,
              reviewCount: reviews.length,
              inventory: product.inventory, // Include inventory data
              dailyInventory: product.dailyInventory, // Include daily inventory data
              sold: product.sold // Include sold data
            };
          } catch (error) {
            console.error(`Error fetching reviews for product ${product.id}:`, error);
            return {
              id: product.id,
              name: product.name,
              image: product.imageUrl || 'https://images.unsplash.com/photo-1621996346565-e3dbc353d2e5?w=400&h=300&fit=crop',
              price: product.unitPrice || product.price || 0,
              stock: product.stock || 0,
              status: product.status !== undefined ? product.status : true,
              published: product.published !== undefined ? product.published : true,
              rating: 0,
              reviewCount: 0,
              inventory: product.inventory,
              dailyInventory: product.dailyInventory,
              sold: product.sold
            };
          }
        })
      );
      
      console.log('🔍 Transformed products:', transformedProducts);
      console.log('🔍 Published count:', transformedProducts.filter(p => p.published).length);
      console.log('🔍 Unpublished count:', transformedProducts.filter(p => !p.published).length);
      
      setProducts(transformedProducts);
    } catch (error) {
      console.error('Error fetching products:', error);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // Refresh onboarding status when seller hub loads - only once
  useEffect(() => {
    if (canAccess && !onboardingLoading && currentUser && !hasCalledFetchStatus.current) {
      hasCalledFetchStatus.current = true; // Mark as called
      // Refresh the onboarding status to ensure setup widget shows current verification status
      fetchStatus().then(() => {
        // Onboarding status refreshed
      });
    }
  }, [canAccess, currentUser, onboardingLoading, fetchStatus]);

  // Helper functions for order filtering
  const getTodayOrders = useCallback(() => {
    if (!orders || orders.length === 0) return [];
    
    const today = new Date();
    const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const todayEnd = new Date(todayStart.getTime() + 24 * 60 * 60 * 1000);
    
    return orders
      .filter(order => {
        const orderDate = new Date(order.createdAt);
        return orderDate >= todayStart && orderDate < todayEnd;
      })
      .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)); // Sort by creation time
  }, [orders]);

  const getThisWeekOrders = useCallback(() => {
    if (!orders || orders.length === 0) return [];
    
    const today = new Date();
    const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const weekStart = new Date(todayStart.getTime() - today.getDay() * 24 * 60 * 60 * 1000);
    const weekEnd = new Date(weekStart.getTime() + 7 * 24 * 60 * 60 * 1000);
    
    return orders
      .filter(order => {
        const orderDate = new Date(order.createdAt);
        return orderDate >= weekStart && orderDate < weekEnd && orderDate >= todayStart; // Exclude today
      })
      .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)); // Sort by creation time
  }, [orders]);

  // Calculate dashboard metrics from orders with memoization
  const calculateDashboardMetrics = useCallback(() => {
    try {
      if (!orders || orders.length === 0) {
        return {
          ordersInProgress: 0,
          totalRevenue: 0,
          averageRating: 0,
          inventoryAlerts: 0,
          products: 0
        };
      }

    // Count orders in progress by checking groups with 'in-progress' or 'ready' status
    const ordersInProgress = orders.reduce((count, order) => {
      // Check if this order has any groups with 'in-progress' or 'ready' status
      const hasInProgressGroup = order.items?.some(item => {
        // Group items by service date to determine group status
        const serviceGroups = {};
        order.items.forEach(item => {
          const serviceDate = item.serviceDate || 'unknown';
          if (!serviceGroups[serviceDate]) {
            serviceGroups[serviceDate] = [];
          }
          serviceGroups[serviceDate].push(item);
        });
        
        // Check each group's status
        return Object.values(serviceGroups).some(groupItems => {
          const groupStatus = getCentralizedGroupStatus(groupItems);
          return groupStatus === 'IN-PROGRESS' || groupStatus === 'READY';
        });
      });
      
      return hasInProgressGroup ? count + 1 : count;
    }, 0);

    // Debug logging for development
    if (process.env.NODE_ENV === 'development') {
      console.log('📊 Orders in progress calculation:', {
        totalOrders: orders.length,
        ordersInProgress,
        orderDetails: orders.map(order => ({
          id: order.id,
          itemCount: order.items?.length || 0,
          groupStatuses: order.items ? Object.values(
            order.items.reduce((groups, item) => {
              const serviceDate = item.serviceDate || 'unknown';
              if (!groups[serviceDate]) groups[serviceDate] = [];
              groups[serviceDate].push(item);
              return groups;
            }, {})
          ).map(groupItems => getCentralizedGroupStatus(groupItems)) : []
        }))
      });
    }

    // Calculate total revenue from completed orders
    const totalRevenue = orders
      .filter(order => ['completed', 'delivered'].includes(order.status))
      .reduce((sum, order) => sum + ((order.total || 0) / 100), 0);

    // Calculate average rating from real ratings data
    const averageRating = ratingsData ? ratingsData.averageRating : 0;
    
    // Debug logging for ratings
    if (process.env.NODE_ENV === 'development') {
      console.log('🔍 SellerHub calculateDashboardMetrics - ratingsData:', ratingsData);
      console.log('🔍 SellerHub calculateDashboardMetrics - averageRating:', averageRating);
    }

    // Calculate real inventory alerts from products data
    const inventoryAlerts = products ? calculateInventoryAlerts(products).total : 0;

    return {
      ordersInProgress,
      totalRevenue,
      averageRating,
      inventoryAlerts,
      products: products ? products.length : 0
    };
    } catch (error) {
      console.error('Error calculating dashboard metrics:', error);
      return {
        ordersInProgress: 0,
        totalRevenue: 0,
        averageRating: 0,
        inventoryAlerts: 0,
        products: 0
      };
    }
  }, [orders, ratingsData, products]);

  // Calculate revenue trend for the last N days with memoization
  const calculateRevenueTrend = useCallback((orders, days = 7) => {
    if (!orders || orders.length === 0) {
      return [];
    }

    const trend = [];
    const today = new Date();
    
    // Generate the last N days
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      
      // Format date for comparison (YYYY-MM-DD)
      const dateString = date.toISOString().split('T')[0];
      
      // Calculate revenue for this specific date
      const dayRevenue = orders
        .filter(order => {
          const orderDate = new Date(order.createdAt);
          const orderDateString = orderDate.toISOString().split('T')[0];
          return orderDateString === dateString;
        })
        .reduce((sum, order) => sum + ((order.total || 0) / 100), 0);
      
      trend.push({
        date: dateString,
        revenue: dayRevenue
      });
    }
    
    return trend;
  }, []);

  // Fetch financial data and calculate performance metrics with memoization
  const fetchFinancialData = useCallback(async () => {
    if (!currentUser?.uid) return;
    
    try {
      setFinancialLoading(true);
      
      // Fetch current month data
      const currentFinancials = await getSellerFinancials(currentUser.uid, '30d');
      
      if (process.env.NODE_ENV === 'development') {
        console.log('Financial data received:', currentFinancials);
      }
      
      // For trend calculation, we would need previous month data
      // For now, we'll use dummy trend data but calculate real current metrics
      if (currentFinancials && currentFinancials.success && currentFinancials.data) {
        setFinancialData(currentFinancials);
        
        // Calculate performance metrics from financial data
        const totalOrders = currentFinancials.data.orderCounts?.total || 0;
        const totalRevenue = currentFinancials.data.revenue?.total || 0;
        const averageOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;
        
        if (process.env.NODE_ENV === 'development') {
          console.log('Calculated metrics:', { totalOrders, totalRevenue, averageOrderValue });
        }
        
        // Update metrics with real data (keeping dummy trend percentages for now)
        const newMetrics = {
          totalRevenue: totalRevenue,
          totalOrders: totalOrders,
          averageOrderValue: averageOrderValue,
          conversionRate: analyticsData ? analyticsData.store.conversionRate : 0,
          revenueChange: 8.2, // This would be calculated from previous period
          ordersChange: 5.4,
          aovChange: -2.1,
          conversionChange: 1.2
        };
        
        if (process.env.NODE_ENV === 'development') {
          console.log('Setting new metrics:', newMetrics);
        }
        setMetrics(newMetrics);
        
        // Calculate revenue trends for the last 7 days
        const last7DaysTrend = calculateRevenueTrend(orders, 7);
        if (process.env.NODE_ENV === 'development') {
          console.log('Calculated revenue trend:', last7DaysTrend);
        }
        setRevenueTrend(last7DaysTrend);
      } else {
        if (process.env.NODE_ENV === 'development') {
          console.log('No financial data available or request failed:', currentFinancials);
        }
        
        // If no financial data, show some realistic placeholder data
        // This helps users see the system working even without orders
        setMetrics({
          totalRevenue: 0,
          totalOrders: 0,
          averageOrderValue: 0,
          conversionRate: analyticsData ? analyticsData.store.conversionRate : 0,
          revenueChange: 0,
          ordersChange: 0,
          aovChange: 0,
          conversionChange: 0
        });
        
        // Set empty revenue trend when no data
        setRevenueTrend([]);
      }
    } catch (error) {
      console.error('Error fetching financial data:', error);
    } finally {
      setFinancialLoading(false);
    }
  }, [currentUser?.uid, analyticsData, orders, calculateRevenueTrend]);

  // Fetch ratings data for the store with memoization
  const fetchRatingsData = useCallback(async () => {
    if (!currentUser?.uid) return;
    
    try {
      console.log('🔍 fetchRatingsData called for user:', currentUser.uid);
      const ratingsResult = await getStoreRatingSummary(currentUser.uid);
      
      // getStoreRatingSummary returns data directly, not wrapped in success/data
      console.log('🔍 Raw ratings result:', ratingsResult);
      
      // Transform the data to match expected format
      const transformedData = {
        averageRating: ratingsResult.avgRating || 0,
        totalReviews: ratingsResult.reviewCount || 0,
        ratingBreakdown: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 }, // TODO: Calculate breakdown
        storeName: ratingsResult.storeName || 'Unknown Store',
        storeId: ratingsResult.storeId,
        lastUpdated: ratingsResult.lastUpdated
      };
      
      console.log('🔍 Transformed ratings data:', transformedData);
      setRatingsData(transformedData);
      
    } catch (error) {
      console.error('❌ Error fetching ratings data:', error);
      // Set default ratings data on error
      setRatingsData({
        averageRating: 0,
        totalReviews: 0,
        ratingBreakdown: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 }
      });
    }
  }, [currentUser?.uid]);

  // Fetch analytics data for the store with memoization and non-blocking
  const fetchAnalyticsData = useCallback(async () => {
    if (!currentUser?.uid) return;
    
    try {
      const analyticsResult = await getSellerAnalytics(currentUser.uid, '30d');
      
      if (analyticsResult.success) {
        setAnalyticsData(analyticsResult.data);
        if (process.env.NODE_ENV === 'development') {
          console.log('Analytics data received:', analyticsResult.data);
        }
      } else {
        if (process.env.NODE_ENV === 'development') {
          console.log('Failed to fetch analytics data:', analyticsResult.error);
        }
        // Set default analytics data if fetch fails
        setAnalyticsData({
          store: {
            views: 0,
            totalOrders: 0,
            conversionRate: 0
          },
          products: [],
          uniqueSessions: 0
        });
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching analytics data:', error);
      }
      // Set default analytics data on error
      setAnalyticsData({
        store: {
          views: 0,
          totalOrders: 0,
          conversionRate: 0
        },
        products: [],
        uniqueSessions: 0
      });
    }
  }, [currentUser?.uid]);

  // Fetch seller's orders with memoization
  const fetchOrders = useCallback(async () => {
    if (!currentUser) return;
    setOrdersLoading(true);
    try {
      const sellerOrders = await getSellerOrdersWithItems(currentUser.uid);
      if (process.env.NODE_ENV === 'development') {
        console.log('Orders fetched:', sellerOrders);
      }
      setOrders(sellerOrders);
    } catch (error) {
      console.error('Error fetching orders:', error);
      setOrders([]);
    } finally {
      setOrdersLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Parallel data fetching for better performance
  useEffect(() => {
    if (currentUser?.uid && canAccess) {
      // Fetch all data in parallel instead of sequentially
      Promise.allSettled([
        fetchFinancialData(),
        fetchAnalyticsData()
      ]).then((results) => {
        if (process.env.NODE_ENV === 'development') {
          console.log('Parallel data fetch completed:', results.map(r => r.status));
        }
      });
    }
  }, [currentUser?.uid, canAccess]);

  // Function to refresh store rating manually
  const handleRefreshStoreRating = async () => {
    if (!currentUser?.uid) return;
    
    try {
      console.log('🔄 Manually refreshing store rating...');
      await refreshStoreRating(currentUser.uid);
      // Refetch the ratings data to update the UI
      await fetchRatingsData();
      console.log('✅ Store rating refreshed and UI updated');
    } catch (error) {
      console.error('❌ Error refreshing store rating:', error);
    }
  };

  // Fetch ratings data for the store (keep separate as it's lightweight)
  useEffect(() => {
    if (currentUser?.uid) {
      fetchRatingsData();
    }
  }, [currentUser?.uid, fetchRatingsData]);

  // Update metrics when analytics data changes
  useEffect(() => {
    if (analyticsData && metrics) {
      setMetrics(prev => ({
        ...prev,
        conversionRate: analyticsData.store.conversionRate
      }));
    }
  }, [analyticsData]);

  function formatOrderNumber(orderId) {
    if (!orderId) return 'Order #000000';
    const shortId = orderId.slice(-6).toUpperCase();
    return `Order #${shortId}`;
  }

  const handleQuickAction = (action) => {
    switch (action) {
      case 'add-product':
        navigate('/create-product-video'); // Navigate to video product creation
        break;
      case 'create-product-video':
        navigate('/create-product-video'); // Navigate to video product creation
        break;
      case 'view-orders':
        navigate('/orders'); // Navigate to orders page
        break;
      case 'manage-discounts':
        navigate('/discounts'); // Navigate to discounts page
        break;
      case 'view-payments':
        navigate('/payments'); // Navigate to payments page
        break;
      case 'legal-center':
        navigate('/legal/seller'); // Navigate to seller legal policies
        break;
      default:
        break;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return WARNING_ORANGE;
      case 'preparing':
        return PRIMARY_BROWN;
      case 'ready':
        return SUCCESS_GREEN;
      case 'delivered':
        return ACCENT_GRAY;
      default:
        return ACCENT_GRAY;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending':
        return 'Pending';
      case 'preparing':
        return 'Preparing';
      case 'ready':
        return 'Ready';
      case 'delivered':
        return 'Delivered';
      default:
        return status;
    }
  };

  const formatCurrency = (amount) => {
    // Handle both cents and dollars - if amount is very large, assume it's in cents
    const value = amount > 1000 ? amount / 100 : amount;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(value);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderTrendArrow = (change) => {
    if (change > 0) {
      return <ArrowUp set="bold" size={16} primaryColor={SUCCESS_GREEN} />;
    } else if (change < 0) {
      return <ArrowDown set="bold" size={16} primaryColor={ERROR_RED} />;
    }
    return null;
  };

  const renderTrendText = (change) => {
    if (change > 0) {
      return `+${change}%`;
    } else if (change < 0) {
      return `${change}%`;
    }
    return '0%';
  };

  const getTrendColor = (change) => {
    if (change > 0) return SUCCESS_GREEN;
    if (change < 0) return ERROR_RED;
    return ACCENT_GRAY;
  };

  const filteredProducts = products.filter(product => {
    switch (activeProductTab) {
      case 'active':
        return product.status === true;
      case 'inactive':
        return product.status === false;
      default:
        return true;
    }
  });

  // Handler for closing shop (to be implemented)
  const handleCloseShop = async () => {
    setShowCloseShopModal(false);
    setShowFinalConfirm(true);
  };

  const handleFinalDelete = async () => {
    setDeleting(true);
    setError('');
    // TODO: Implement backend deletion logic here
    // On success:
    // navigate('/customer-dashboard', { state: { message: 'Your shop has been permanently closed.' } });
    // On error:
    // setError('Failed to close shop. Please try again.');
    setDeleting(false);
  };

  // Handler for updating order status
  const handleOrderStatusUpdate = async (merchantOrderId, newStatus) => {
    if (!currentUser) return;
    
    try {
      console.log(`🔄 Updating order ${merchantOrderId} to status: ${newStatus}`);
      
      const result = await updateMerchantOrderStatus(merchantOrderId, newStatus, currentUser.uid);
      
      if (result.success) {
        console.log(`✅ Order status updated successfully: ${result.message}`);
        
        // Refresh orders to show updated status
        const updatedOrders = await getSellerMerchantOrders(currentUser.uid);
        setOrders(updatedOrders);
        
        // Show success message (you could add a toast notification here)
        console.log(`📧 Email alert sent to customer for status: ${newStatus}`);
      }
      
    } catch (error) {
      console.error('❌ Error updating order status:', error);
      setError(`Failed to update order status: ${error.message}`);
    }
  };

  useEffect(() => {
    const loadOrders = async () => {
      if (!currentUser) return;
      
      try {
        setLoading(true);
        const merchantOrders = await getSellerMerchantOrders(currentUser.uid);
        setOrders(merchantOrders);
      } catch (err) {
        console.error('Error loading orders:', err);
        setError('Failed to load orders');
      } finally {
        setLoading(false);
      }
    };

    loadOrders();
  }, [currentUser]);

  const getDateLabel = (serviceDate) => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    const orderDate = new Date(serviceDate + 'T00:00:00');
    
    if (orderDate.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (orderDate.toDateString() === tomorrow.toDateString()) {
      return 'Tomorrow';
    } else {
      return orderDate.toLocaleDateString('en-US', { 
        weekday: 'short', 
        month: 'short', 
        day: 'numeric' 
      });
    }
  };

  const getDateGroups = () => {
    const groups = {};
    
    orders.forEach(merchantOrder => {
      merchantOrder.items?.forEach(item => {
        // Create unique group key by combining date and merchant order ID
        const groupKey = `${item.serviceDate}-${merchantOrder.id}`;
        if (!groups[groupKey]) {
          groups[groupKey] = {
            date: item.serviceDate,
            label: getDateLabel(item.serviceDate),
            merchantOrders: new Set(),
            items: [],
            parentOrderId: merchantOrder.parentOrderId, // Include parentOrderId for navigation
            merchantOrderId: merchantOrder.id // Include merchant order ID
          };
        }
        groups[groupKey].merchantOrders.add(merchantOrder.id);
        groups[groupKey].items.push(item);
      });
    });

    const allGroups = Object.values(groups).map(group => ({
      ...group,
      merchantOrders: Array.from(group.merchantOrders),
      merchantOrderCount: group.merchantOrders.size,
      itemCount: group.items.length
    }));

    console.log('[SellerHub] All groups created:', allGroups.map(g => ({ 
      date: g.date, 
      merchantOrderId: g.merchantOrderId, 
      itemCount: g.itemCount,
      itemStatuses: g.items.map(item => item.status)
    })));
    
    // Log Sep 21 groups specifically
    const sep21Groups = allGroups.filter(g => g.date === '2025-09-21');
    console.log('[SellerHub] Sep 21 groups:', sep21Groups.map(g => ({ 
      date: g.date, 
      merchantOrderId: g.merchantOrderId, 
      itemCount: g.itemCount,
      itemStatuses: g.items.map(item => item.status)
    })));

    return allGroups;
  };

  const getTodayGroups = () => {
    const today = new Date().toISOString().split('T')[0];
    return getDateGroups().filter(group => group.date === today);
  };

  const getUpcomingGroups = () => {
    const today = new Date().toISOString().split('T')[0];
    const allGroups = getDateGroups();
    const upcomingGroups = allGroups
      .filter(group => group.date > today)
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .slice(0, 5);
    
    console.log('[SellerHub] Today date:', today);
    console.log('[SellerHub] Upcoming filter - all groups dates:', allGroups.map(g => g.date));
    const groupsAfterDateFilter = allGroups.filter(g => g.date > today);
    console.log('[SellerHub] Upcoming filter - groups with date > today:', groupsAfterDateFilter.map(g => ({ 
      date: g.date, 
      merchantOrderId: g.merchantOrderId,
      itemStatuses: g.items.map(item => item.status)
    })));
    
    // Sort groups: accepted orders first, then by date
    const sortedGroups = groupsAfterDateFilter.sort((a, b) => {
      const aHasAccepted = a.items.some(item => item.status === 'accepted');
      const bHasAccepted = b.items.some(item => item.status === 'accepted');
      
      console.log(`[SellerHub] Sorting: ${a.date} (hasAccepted: ${aHasAccepted}) vs ${b.date} (hasAccepted: ${bHasAccepted})`);
      
      // First priority: groups with accepted status
      if (aHasAccepted && !bHasAccepted) {
        console.log(`[SellerHub] Returning -1: ${a.date} (accepted) comes before ${b.date} (not accepted)`);
        return -1;
      }
      if (!aHasAccepted && bHasAccepted) {
        console.log(`[SellerHub] Returning 1: ${b.date} (accepted) comes before ${a.date} (not accepted)`);
        return 1;
      }
      
      // Second priority: if both have same acceptance status, sort by date (earliest first)
      const dateComparison = new Date(a.date) - new Date(b.date);
      console.log(`[SellerHub] Same priority, sorting by date: ${a.date} vs ${b.date} = ${dateComparison}`);
      return dateComparison;
    });
    
    // Debug: Show the exact order of all groups after sorting
    console.log('[SellerHub] All groups after sorting (first 5):', sortedGroups.slice(0, 5).map((g, index) => ({
      index,
      date: g.date, 
      merchantOrderId: g.merchantOrderId,
      hasAccepted: g.items.some(item => item.status === 'accepted'),
      itemStatuses: g.items.map(item => item.status)
    })));
    
    // Debug: Show the exact order of all groups after sorting (first 10)
    console.log('[SellerHub] All groups after sorting (first 10):', sortedGroups.slice(0, 10).map((g, index) => ({
      index,
      date: g.date, 
      merchantOrderId: g.merchantOrderId,
      hasAccepted: g.items.some(item => item.status === 'accepted'),
      itemStatuses: g.items.map(item => item.status)
    })));
    console.log('[SellerHub] Upcoming filter - after sorting:', sortedGroups.map(g => ({ date: g.date, merchantOrderId: g.merchantOrderId })));
    console.log('[SellerHub] Upcoming filter - first 3 groups:', sortedGroups.slice(0, 3).map(g => ({ 
      date: g.date, 
      merchantOrderId: g.merchantOrderId,
      hasAccepted: g.items.some(item => item.status === 'accepted'),
      itemStatuses: g.items.map(item => item.status)
    })));
    
    // Debug: Show the exact order of all groups after sorting
    console.log('[SellerHub] All groups after sorting (first 5):', sortedGroups.slice(0, 5).map((g, index) => ({
      index,
      date: g.date, 
      merchantOrderId: g.merchantOrderId,
      hasAccepted: g.items.some(item => item.status === 'accepted'),
      itemStatuses: g.items.map(item => item.status)
    })));
    
    // Debug: Show the exact order of all groups after sorting (first 10)
    console.log('[SellerHub] All groups after sorting (first 10):', sortedGroups.slice(0, 10).map((g, index) => ({
      index,
      date: g.date, 
      merchantOrderId: g.merchantOrderId,
      hasAccepted: g.items.some(item => item.status === 'accepted'),
      itemStatuses: g.items.map(item => item.status)
    })));
    
    console.log('[SellerHub] Upcoming filter - final upcoming groups:', upcomingGroups.map(g => ({ 
      date: g.date, 
      merchantOrderId: g.merchantOrderId,
      hasAccepted: g.items.some(item => item.status === 'accepted'),
      itemStatuses: g.items.map(item => item.status)
    })));
    
    // Debug: Check if Sep 21 groups are in the sorted array
    const sep21InSorted = sortedGroups.filter(g => g.date === '2025-09-21');
    console.log('[SellerHub] Sep 21 groups in sorted array:', sep21InSorted.map(g => ({
      date: g.date,
      merchantOrderId: g.merchantOrderId,
      hasAccepted: g.items.some(item => item.status === 'accepted'),
      itemStatuses: g.items.map(item => item.status)
    })));
    
    // Debug: Check if Sep 21 groups are in the final upcoming groups
    const sep21InUpcoming = upcomingGroups.filter(g => g.date === '2025-09-21');
    console.log('[SellerHub] Sep 21 groups in final upcoming groups:', sep21InUpcoming.map(g => ({
      date: g.date,
      merchantOrderId: g.merchantOrderId,
      hasAccepted: g.items.some(item => item.status === 'accepted'),
      itemStatuses: g.items.map(item => item.status)
    })));
    
    return upcomingGroups;
  };

  const getGroupStatus = (items) => {
    return getCentralizedGroupStatus(items);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#C97B53] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading seller hub...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">❌</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Error Loading Hub</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-[#C97B53] text-white px-6 py-3 rounded-lg hover:bg-[#B86A42] transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const todayGroups = getTodayGroups();
  const upcomingGroups = getUpcomingGroups();

  // Debug logging for mobile
  console.log('SellerHub Debug:', { 
    onboardingLoading, 
    canAccess, 
    currentUser: !!currentUser,
    showLoading: onboardingLoading || !canAccess,
    isMobile,
    windowWidth: window.innerWidth
  });

  return (
    <div style={{ minHeight: '100vh', background: NEUTRAL_BG, paddingBottom: '80px' }}>
      {/* Loading Spinner - show loading until all checks are complete */}
      {(onboardingLoading || !canAccess) && (
        <>
          <style dangerouslySetInnerHTML={{
            __html: `
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `
          }} />
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100vh',
            background: NEUTRAL_BG
          }}>
            <div style={{ 
              width: 32, 
              height: 32, 
              border: '3px solid transparent', 
              borderTop: '3px solid #C97B53', 
              borderRadius: '50%', 
              animation: 'spin 1s linear infinite' 
            }} />
          </div>
        </>
      )}

      {/* Access Denied - only show after loading is complete */}
      {!onboardingLoading && !canAccess && (
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          background: NEUTRAL_BG,
          padding: '20px'
        }}>
          <h2 style={{ color: NEUTRAL_TEXT, marginBottom: '20px' }}>
            Access Denied - Setup Required
          </h2>
          <p style={{ color: ACCENT_GRAY, marginBottom: '20px', textAlign: 'center' }}>
            You need to complete the setup fee payment before accessing the seller hub.
          </p>
          <button
            onClick={() => navigate('/onboarding')}
            style={{
              background: PRIMARY_BROWN,
              color: NEUTRAL_WHITE,
              border: 'none',
              borderRadius: '8px',
              padding: '12px 24px',
              fontSize: '12px',
              cursor: 'pointer'
            }}
          >
            Go to Onboarding
          </button>
          <button
            onClick={async () => {
              const hasAccess = await canAccessSellerHubDirect();
              setCanAccess(hasAccess);
            }}
            style={{
              background: 'transparent',
              color: PRIMARY_BROWN,
              border: `1px solid ${PRIMARY_BROWN}`,
              borderRadius: '8px',
              padding: '12px 24px',
              fontSize: '12px',
              cursor: 'pointer',
              marginTop: '12px'
            }}
          >
            Check Access Again
          </button>
          <button
            onClick={async () => {
              await debugSetupFeeStatus();
            }}
            style={{
              background: 'transparent',
              color: ACCENT_GRAY,
              border: `1px solid ${ACCENT_GRAY}`,
              borderRadius: '8px',
              padding: '12px 24px',
              fontSize: '12px',
              cursor: 'pointer',
              marginTop: '12px'
            }}
          >
            Debug Setup Fee Status
          </button>
        </div>
      )}

      {/* Main Content - always show */}
      <>
        <SellerHeader />

          {/* Access Denied Message */}
          {!canAccess && (
            <div style={{ 
              padding: '40px 20px', 
              textAlign: 'center',
              background: NEUTRAL_BG,
              minHeight: 'calc(100vh - 200px)',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center'
            }}>
              <div style={{ 
                fontSize: 24, 
                fontWeight: 700, 
                color: NEUTRAL_TEXT, 
                marginBottom: 16,
                ...headingFont 
              }}>
                Access Required
              </div>
              <div style={{ 
                fontSize: 16, 
                color: ACCENT_GRAY, 
                marginBottom: 24,
                ...bodyFont 
              }}>
                Please complete your seller setup to access the dashboard.
              </div>
              <button
                onClick={() => navigate('/onboarding')}
                style={{
                  background: PRIMARY_BROWN,
                  color: NEUTRAL_WHITE,
                  border: 'none',
                  borderRadius: 12,
                  padding: '16px 32px',
                  fontSize: 16,
                  fontWeight: 600,
                  cursor: 'pointer',
                  ...bodyFont
                }}
              >
                Complete Setup
              </button>
            </div>
          )}

          {/* Main Dashboard Content - only show when access is confirmed */}
          {canAccess && (
          <div style={{ paddingTop: '120px' }}>
            {/* Side Navigation */}
            <SideNavigation 
              onQuickAction={setActiveAction}
              activeAction={activeAction}
              storeId={currentUser?.uid}
            />
            
            {/* Content Area - Adjusted for side navigation */}
            <div style={{ marginLeft: isQuickActionsOpen ? '280px' : '0px' }}>
              
              {/* Dashboard Content */}
              <div>
              {/* Dashboard Metrics */}
              <div style={{ padding: '20px 20px 0' }}>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(2, 1fr)', 
                  gap: 16,
                  marginBottom: 24
                }}>
                  {/* Orders in Progress */}
                  <div style={{
                    background: NEUTRAL_WHITE,
                    borderRadius: 12,
                    padding: '20px 16px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    textAlign: 'center'
                  }}>
                    <div style={{ 
                      fontSize: 12, 
                      color: ACCENT_GRAY, 
                      marginBottom: 8,
                      ...bodyFont 
                    }}>
                      Orders in Progress
                    </div>
                    <div style={{ 
                      fontSize: 32, 
                      fontWeight: 700, 
                      background: COLORS.PRIMARY_GRADIENT,
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                      ...headingFont 
                    }}>
                      {(calculateDashboardMetrics()?.ordersInProgress) || 0}
                    </div>
                  </div>

                  {/* Sales (Aug) */}
                  <div style={{
                    background: NEUTRAL_WHITE,
                    borderRadius: 12,
                    padding: '20px 16px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    textAlign: 'center'
                  }}>
                    <div style={{ 
                      fontSize: 12, 
                      color: ACCENT_GRAY, 
                      marginBottom: 8,
                      ...bodyFont 
                    }}>
                      Total Revenue
                    </div>
                    <div style={{ 
                      fontSize: 32, 
                      fontWeight: 700, 
                      background: COLORS.PRIMARY_GRADIENT,
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                      ...headingFont 
                    }}>
                      {formatCurrency((calculateDashboardMetrics()?.totalRevenue) || 0)}
                    </div>
                  </div>

                  {/* Rating */}
                  <button
                    onClick={async () => {
                      // First refresh the store rating
                      await handleRefreshStoreRating();
                      // Then navigate to the reviews page
                      navigate(`/storefront/${currentUser?.uid}/reviews`);
                    }}
                    style={{
                      background: NEUTRAL_WHITE,
                      borderRadius: 12,
                      padding: '20px 16px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      textAlign: 'center',
                      border: 'none',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      width: '100%'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.15)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                    }}
                  >
                    <div style={{ 
                      fontSize: 12, 
                      color: ACCENT_GRAY, 
                      marginBottom: 8,
                      ...bodyFont 
                    }}>
                      Rating
                    </div>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      gap: 8,
                      marginBottom: 4
                    }}>
                      <span style={{ 
                        fontSize: 32, 
                        fontWeight: 700, 
                        background: COLORS.PRIMARY_GRADIENT,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                        ...headingFont 
                      }}>
                        {(calculateDashboardMetrics()?.averageRating) || 0}
                      </span>
                      <Star set="bold" size={24} primaryColor={COLORS.PRIMARY_BROWN} />
                    </div>
                    <div style={{ 
                      fontSize: 10, 
                      color: ACCENT_GRAY,
                      ...bodyFont 
                    }}>
                      {ratingsData ? `${ratingsData.totalReviews} review${ratingsData.totalReviews !== 1 ? 's' : ''}` : '0 reviews'}
                    </div>
                  </button>

                  {/* Inventory Alerts */}
                  <button
                    onClick={() => navigate('/all-products?filter=needsAttention')}
                    style={{
                      background: NEUTRAL_WHITE,
                      borderRadius: 12,
                      padding: '20px 16px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      textAlign: 'center',
                      border: 'none',
                      cursor: 'pointer',
                      width: '100%',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.15)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                    }}
                  >
                    <div style={{ 
                      fontSize: 12, 
                      color: ACCENT_GRAY, 
                      marginBottom: 8,
                      ...bodyFont 
                    }}>
                      Inventory Alerts
                    </div>
                    <div style={{ 
                      fontSize: 32, 
                      fontWeight: 700, 
                      background: COLORS.PRIMARY_GRADIENT,
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                      ...headingFont 
                    }}>
                      {(calculateDashboardMetrics()?.inventoryAlerts) || 0}
                    </div>
                  </button>
                </div>
              </div>

              {/* Orders - Grouped by Date */}
              <div style={{ padding: '0 20px 20px' }}>
                <div style={{ 
                  marginBottom: 16 
                }}>
                  <h3 style={{ 
                    ...headingFont, 
                    fontSize: 16, 
                    color: NEUTRAL_TEXT, 
                    margin: 0 
                  }}>
                    Orders by Date
                  </h3>
                  
                  {/* Date Group Tabs */}
                  <div style={{ 
                    display: 'flex', 
                    gap: 8, 
                    marginTop: 12 
                  }}>
                    {(() => {
                      const todayGroups = getTodayGroups();
                      const upcomingGroups = getUpcomingGroups();
                      
                      return [
                        { id: 'today', name: 'Today', count: todayGroups.length, filter: 'today' },
                        { id: 'upcoming', name: 'Upcoming', count: upcomingGroups.length, filter: 'upcoming' }
                      ].map(tab => (
                        <button
                          key={tab.id}
                          onClick={() => navigate(`/seller-merchant-orders?filter=${tab.filter}`)}
                          style={{
                            background: 'transparent',
                            color: ACCENT_GRAY,
                            border: 'none',
                            borderRadius: 20,
                            padding: '6px 12px',
                            fontSize: 14,
                            fontWeight: 600,
                            cursor: 'pointer',
                            ...bodyFont
                          }}
                        >
                          {tab.name} ({tab.count})
                        </button>
                      ));
                    })()}
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {ordersLoading ? (
                    <div style={{ background: NEUTRAL_WHITE, borderRadius: 12, padding: 20, textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                      <div style={{ fontSize: 12, color: ACCENT_GRAY, ...bodyFont }}>Loading orders...</div>
                    </div>
                  ) : orders.length === 0 ? (
                    <div style={{ background: NEUTRAL_WHITE, borderRadius: 12, padding: 20, textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                      <div style={{ fontSize: '12', color: ACCENT_GRAY, ...bodyFont }}>No orders found</div>
                    </div>
                  ) : (
                    (() => {
                      const todayGroups = getTodayGroups();
                      const upcomingGroups = getUpcomingGroups();
                      const displayGroups = [...todayGroups, ...upcomingGroups].slice(0, 5);
                      
                      console.log('[SellerHub] Today groups:', todayGroups.map(g => ({ date: g.date, merchantOrderId: g.merchantOrderId })));
                      console.log('[SellerHub] Upcoming groups:', upcomingGroups.map(g => ({ date: g.date, merchantOrderId: g.merchantOrderId })));
                      console.log('[SellerHub] Display groups:', displayGroups.map(g => ({ date: g.date, merchantOrderId: g.merchantOrderId })));
                      
                      return displayGroups.map((group, index) => (
                        <div
                          key={`${group.date}-${group.merchantOrderId || index}`}
                          onClick={() => navigate(`/order-details/${group.parentOrderId}`)}
                          style={{
                            background: NEUTRAL_WHITE,
                            borderRadius: 12,
                            padding: 16,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            cursor: 'pointer',
                            transition: 'box-shadow 0.15s',
                            position: 'relative'
                          }}
                          onMouseOver={e => e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.13)'}
                          onMouseOut={e => e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)'}
                        >
                          {/* Status Chip and Action Button - Upper Right */}
                          <div style={{
                            position: 'absolute',
                            top: 12,
                            right: 12,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-end',
                            gap: 8
                          }}>
                            {/* Status Chip */}
                            <div style={{
                              padding: '4px 12px',
                              background: '#F5F5F5',
                              color: '#666666',
                              borderRadius: 16,
                              fontSize: 12,
                              fontWeight: 600,
                              ...bodyFont
                            }}>
                              {(() => {
                                const status = getGroupStatus(group.items);
                                console.log('[SellerHub] Group for', group.date, 'merchantOrderId:', group.merchantOrderId);
                                console.log('[SellerHub] Items:', group.items.map(item => ({ 
                                  id: item.id, 
                                  status: item.status,
                                  merchantOrderId: item.merchantOrderId,
                                  serviceDate: item.serviceDate
                                })));
                                console.log('[SellerHub] Calculated status:', status);
                                return status.toUpperCase();
                              })()}
                            </div>
                            
                            {/* Action Button */}
                            {(() => {
                              const currentStatus = getGroupStatus(group.items).toLowerCase();
                              const workflow = STATUS_WORKFLOW[currentStatus];
                              
                              if (workflow && workflow.action && group.merchantOrderId) {
                                return (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation(); // Prevent card click
                                      handleOrderStatusUpdate(group.merchantOrderId, workflow.next.toUpperCase());
                                    }}
                                    style={{
                                      ...GRADIENT_UTILS.getGradientButton(),
                                      borderRadius: 8,
                                      padding: '6px 12px',
                                      fontSize: 11,
                                      fontWeight: 600,
                                      cursor: 'pointer',
                                      transition: 'all 0.2s ease',
                                      ...bodyFont
                                    }}
                                    onMouseOver={(e) => {
                                      e.target.style.transform = 'scale(1.05)';
                                      e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                                    }}
                                    onMouseOut={(e) => {
                                      e.target.style.transform = 'scale(1)';
                                      e.target.style.boxShadow = 'none';
                                    }}
                                  >
                                    {workflow.action}
                                  </button>
                                );
                              }
                              return null;
                            })()}
                          </div>
                          
                          {/* Date and Time Display */}
                          <div style={{
                            fontSize: 14,
                            fontWeight: 600,
                            color: NEUTRAL_TEXT,
                            marginBottom: 8,
                            ...bodyFont
                          }}>
                            {(() => {
                              const fulfillmentDate = new Date(group.date + 'T00:00:00');
                              const dateStr = fulfillmentDate.toLocaleDateString('en-US', { 
                                weekday: 'short', 
                                month: 'short', 
                                day: 'numeric' 
                              });
                              
                              // Convert 24-hour format to 12-hour format
                              const formatTime = (timeStr) => {
                                const [hours, minutes] = timeStr.split(':');
                                const hour24 = parseInt(hours);
                                const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
                                const ampm = hour24 >= 12 ? 'PM' : 'AM';
                                return `${hour12}:${minutes} ${ampm}`;
                              };
                              
                              const startTime = formatTime(group.items[0]?.windowStartLocal || '12:00');
                              const endTime = formatTime(group.items[0]?.windowEndLocal || '14:00');
                              
                              return `${dateStr} • ${startTime} - ${endTime}`;
                            })()}
                          </div>
                          
                          {/* Group Details */}
                          <div style={{ flex: 1 }}>
                            {/* Product List - Each on its own line */}
                            <div style={{ 
                              fontSize: 11, 
                              color: NEUTRAL_TEXT,
                              marginBottom: 8,
                              ...bodyFont
                            }}>
                              {(() => {
                                // Group items by product name + variant to get quantities
                                const productMap = new Map();
                                group.items.forEach(item => {
                                  const productName = item.productSnapshot?.name || 'Product';
                                  const variant = item.productSnapshot?.variant && Object.keys(item.productSnapshot.variant).length > 0
                                    ? ` (${Object.entries(item.productSnapshot.variant)
                                        .map(([key, value]) => `${key}: ${value}`)
                                        .join(', ')})`
                                    : '';
                                  const key = `${productName}${variant}`;
                                  
                                  if (productMap.has(key)) {
                                    productMap.set(key, productMap.get(key) + (item.quantity || 1));
                                  } else {
                                    productMap.set(key, item.quantity || 1);
                                  }
                                });
                                
                                const productEntries = Array.from(productMap.entries());
                                return productEntries.map(([productKey, qty], index) => (
                                  <div key={index} style={{ marginBottom: 2 }}>
                                    {qty} × {productKey}
                                  </div>
                                ));
                              })()}
                            </div>
                            
                          </div>
                        </div>
                      ));
                    })()
                  )}
                </div>
              </div>



              {/* Products Summary */}
              <div style={{ padding: '0 20px 20px' }}>
                <div style={{ 
                  marginBottom: 16 
                }}>
                  <h3 style={{ 
                    ...headingFont, 
                    fontSize: 16, 
                    color: NEUTRAL_TEXT, 
                    margin: 0 
                  }}>
                    Products
                  </h3>
                </div>

                {/* Product Tabs */}
                <div style={{ 
                  display: 'flex', 
                  gap: 8, 
                  marginBottom: 16 
                }}>
                  {[
                    { id: 'all', name: 'All Products', count: loading ? '...' : products.length, filter: '' },
                    { id: 'active', name: 'Active', count: loading ? '...' : products.filter(p => p.status === true).length, filter: 'active' },
                    { id: 'inactive', name: 'Inactive', count: loading ? '...' : products.filter(p => p.status === false).length, filter: 'inactive' }
                  ].map(tab => (
                    <button
                      key={tab.id}
                      onClick={() => {
                        setActiveProductTab(tab.id);
                        navigate(`/all-products${tab.filter ? `?status=${tab.filter}` : ''}`);
                      }}
                      style={{
                        background: 'transparent',
                        color: activeProductTab === tab.id ? PRIMARY_BROWN : ACCENT_GRAY,
                        border: 'none',
                        borderRadius: 20,
                        padding: '8px 16px',
                        fontSize: 14,
                        fontWeight: activeProductTab === tab.id ? 700 : 600,
                        cursor: 'pointer',
                        ...bodyFont
                      }}
                    >
                      {tab.name} ({tab.count})
                    </button>
                  ))}
                </div>

                {/* Products List */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {loading ? (
                    <div style={{
                      background: NEUTRAL_WHITE,
                      borderRadius: 12,
                      padding: 20,
                      textAlign: 'center',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                    }}>
                      <div style={{ 
                        fontSize: 14, 
                        color: ACCENT_GRAY,
                        ...bodyFont
                      }}>
                        Loading products...
                      </div>
                    </div>
                  ) : filteredProducts.length === 0 ? (
                    <div style={{
                      background: NEUTRAL_WHITE,
                      borderRadius: 12,
                      padding: 20,
                      textAlign: 'center',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                    }}>
                      <div style={{ 
                        fontSize: 14, 
                        color: ACCENT_GRAY,
                        marginBottom: 8,
                        ...bodyFont
                      }}>
                        No products found
                      </div>
                      <div style={{ 
                        fontSize: 12, 
                        color: ACCENT_GRAY,
                        ...bodyFont
                      }}>
                        Start by adding your first product
                      </div>
                    </div>
                  ) : (
                    filteredProducts.slice(0, 3).map(product => (
                      <div
                        key={product.id}
                        style={{
                          background: NEUTRAL_WHITE,
                          borderRadius: 12,
                          padding: 12,
                          display: 'flex',
                          gap: 12,
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                        }}
                      >
                        <img
                          src={product.image}
                          alt={product.name}
                          style={{
                            width: 60,
                            height: 60,
                            borderRadius: 8,
                            objectFit: 'cover'
                          }}
                        />
                        <div style={{ flex: 1 }}>
                          <h4 style={{ 
                            ...headingFont, 
                            fontSize: 14, 
                            color: NEUTRAL_TEXT,
                            margin: '0 0 4px 0'
                          }}>
                            {product.name}
                          </h4>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                            <Star set="regular" size={12} primaryColor={WARNING_ORANGE} />
                            <span style={{ fontSize: 12, color: NEUTRAL_TEXT, fontWeight: 600 }}>
                              {product.rating}
                            </span>
                          </div>
                          <div style={{ 
                            fontSize: 12, 
                            color: ACCENT_GRAY,
                            ...bodyFont
                          }}>
                            {(() => {
                              // Get next 7 days data
                              const getNext7DaysData = (product) => {
                                const today = new Date();
                                const next7Days = [];
                                
                                for (let i = 0; i < 7; i++) {
                                  const date = new Date(today);
                                  date.setDate(today.getDate() + i);
                                  const dateString = date.toISOString().split('T')[0];
                                  
                                  // Check if product has new inventory structure
                                  if (product.inventory && product.inventory.scheduled) {
                                    // Handle both array and object structures
                                    let scheduledData = null;
                                    
                                    if (Array.isArray(product.inventory.scheduled)) {
                                      // Array structure
                                      scheduledData = product.inventory.scheduled.find(s => s.service_date === dateString);
                                    } else {
                                      // Object structure - iterate through values
                                      const scheduledValues = Object.values(product.inventory.scheduled);
                                      scheduledData = scheduledValues.find(s => s.service_date === dateString);
                                    }
                                    
                                    if (scheduledData) {
                                      next7Days.push({
                                        date: dateString,
                                        available: scheduledData.available_total || 0,
                                        sold: scheduledData.ordered_total || 0
                                      });
                                    } else {
                                      next7Days.push({
                                        date: dateString,
                                        available: 0,
                                        sold: 0
                                      });
                                    }
                                  } else {
                                    // Fallback to legacy stock calculation
                                    const dailyStock = product.dailyInventory?.[dateString] || 0;
                                    const dailySold = product.dailySold?.[dateString] || 0;
                                    
                                    // If no daily inventory, use total stock divided by 7 days
                                    const availableStock = dailyStock || (product.stock ? Math.floor(product.stock / 7) : 0);
                                    const soldStock = dailySold || (product.sold ? Math.floor(product.sold / 7) : 0);
                                    
                                    next7Days.push({
                                      date: dateString,
                                      available: Number(availableStock) || 0,
                                      sold: Number(soldStock) || 0
                                    });
                                  }
                                }
                                
                                return next7Days;
                              };
                              
                              const next7DaysData = getNext7DaysData(product);
                              const totalAvailable = next7DaysData.reduce((sum, day) => sum + day.available, 0);
                              const totalSold = next7DaysData.reduce((sum, day) => sum + day.sold, 0);
                              
                              
                              return `Next 7 days: ${totalAvailable} available • ${totalSold} sold`;
                            })()}
                          </div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ 
                            ...headingFont, 
                            fontSize: 16, 
                            color: NEUTRAL_TEXT 
                          }}>
                            {formatCurrency(product.price)}
                          </div>
                          <div style={{ 
                            fontSize: 12, 
                            color: product.status === true ? SUCCESS_GREEN : ACCENT_GRAY,
                            fontWeight: 600,
                            textTransform: 'capitalize',
                            ...bodyFont
                          }}>
                            {product.status ? 'Active' : 'Inactive'}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Performance Overview */}
              <div style={{ padding: '20px' }}>
                <h2 style={{ 
                  ...headingFont, 
                  fontSize: 18, 
                  color: NEUTRAL_TEXT, 
                  marginBottom: 16 
                }}>
                  Performance Overview
                </h2>
                
                {financialLoading ? (
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(2, 1fr)', 
                    gap: 12 
                  }}>
                    {[1, 2, 3, 4].map((i) => (
                      <div key={i} style={{
                        background: NEUTRAL_WHITE,
                        borderRadius: 16,
                        padding: 16,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 8
                      }}>
                        <div style={{
                          height: 12,
                          background: '#f0f0f0',
                          borderRadius: 6,
                          width: '60%'
                        }} />
                        <div style={{
                          height: 24,
                          background: '#f0f0f0',
                          borderRadius: 6,
                          width: '80%'
                        }} />
                        <div style={{
                          height: 12,
                          background: '#f0f0f0',
                          borderRadius: 6,
                          width: '50%'
                        }} />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(2, 1fr)', 
                    gap: 12 
                  }}>
                  {/* Total Revenue */}
                  <div style={{
                    background: NEUTRAL_WHITE,
                    borderRadius: 16,
                    padding: 16,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <span style={{ fontSize: 12, color: ACCENT_GRAY, ...bodyFont }}>
                        Total Revenue
                      </span>
                      {renderTrendArrow(metrics?.revenueChange || 0)}
                    </div>
                    <div style={{ 
                      ...headingFont, 
                      fontSize: 24, 
                      color: NEUTRAL_TEXT,
                      marginBottom: 4
                    }}>
                      {formatCurrency(metrics?.totalRevenue || 0)}
                    </div>
                    <div style={{ 
                      fontSize: 12, 
                      color: getTrendColor(metrics?.revenueChange || 0),
                      fontWeight: 600,
                      ...bodyFont
                    }}>
                      {renderTrendText(metrics?.revenueChange || 0)} from last month
                    </div>
                  </div>

                  {/* Total Orders */}
                  <div style={{
                    background: NEUTRAL_WHITE,
                    borderRadius: 16,
                    padding: 16,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <span style={{ fontSize: 12, color: ACCENT_GRAY, ...bodyFont }}>
                        Total Orders
                      </span>
                      {renderTrendArrow(metrics?.ordersChange || 0)}
                    </div>
                    <div style={{ 
                      ...headingFont, 
                      fontSize: 24, 
                      color: NEUTRAL_TEXT,
                      marginBottom: 4
                    }}>
                      {(metrics?.totalOrders) || 0}
                    </div>
                    <div style={{ 
                      fontSize: 12, 
                      color: getTrendColor(metrics?.ordersChange || 0),
                      fontWeight: 600,
                      ...bodyFont
                    }}>
                      {renderTrendText(metrics?.ordersChange || 0)} from last month
                    </div>
                  </div>

                  {/* Average Order Value */}
                  <div style={{
                    background: NEUTRAL_WHITE,
                    borderRadius: 16,
                    padding: 16,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <span style={{ fontSize: 12, color: ACCENT_GRAY, ...bodyFont }}>
                        Avg Order Value
                      </span>
                      {renderTrendArrow(metrics?.aovChange || 0)}
                    </div>
                    <div style={{ 
                      ...headingFont, 
                      fontSize: 24, 
                      color: NEUTRAL_TEXT,
                      marginBottom: 4
                    }}>
                      {formatCurrency(metrics?.averageOrderValue || 0)}
                    </div>
                    <div style={{ 
                      fontSize: 12, 
                      color: getTrendColor(metrics?.aovChange || 0),
                      fontWeight: 600,
                      ...bodyFont
                    }}>
                      {renderTrendText(metrics?.aovChange || 0)} from last month
                    </div>
                  </div>

                  {/* Conversion Rate */}
                  <div style={{
                    background: NEUTRAL_WHITE,
                    borderRadius: 16,
                    padding: 16,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <span style={{ fontSize: 12, color: ACCENT_GRAY, ...bodyFont }}>
                        Conversion Rate
                      </span>
                      {renderTrendArrow(metrics?.conversionChange || 0)}
                    </div>
                    <div style={{ 
                      ...headingFont, 
                      fontSize: 24, 
                      color: NEUTRAL_TEXT,
                      marginBottom: 4
                    }}>
                      {(metrics?.conversionRate || 0)}%
                    </div>
                    <div style={{ 
                      fontSize: 12, 
                      color: getTrendColor(metrics?.conversionChange || 0),
                      fontWeight: 600,
                      ...bodyFont
                    }}>
                      {renderTrendText(metrics?.conversionChange || 0)} from last month
                    </div>
                  </div>
                </div>
                )}
              </div>

              {/* Revenue Trend Graph */}
              <div style={{ padding: '0 20px 20px' }}>
                <div style={{
                  background: NEUTRAL_WHITE,
                  borderRadius: 16,
                  padding: 20,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}>
                  <h3 style={{ 
                    ...headingFont, 
                    fontSize: 18, 
                    color: NEUTRAL_TEXT, 
                    marginBottom: 16 
                  }}>
                    Revenue Trend (Last 7 Days)
                  </h3>
                  
                  {/* Simple bar chart representation */}
                  {revenueTrend.length > 0 ? (
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'end', 
                      gap: 8, 
                      height: 120,
                      paddingTop: 20
                    }}>
                      {revenueTrend.map((day, index) => {
                        const maxRevenue = Math.max(...revenueTrend.map(d => d.revenue));
                        const height = maxRevenue > 0 ? (day.revenue / maxRevenue) * 80 : 0;
                        return (
                          <div key={index} style={{ flex: 1, textAlign: 'center' }}>
                            <div style={{
                              background: COLORS.PRIMARY_GRADIENT,
                              height: height,
                              borderRadius: '4px 4px 0 0',
                              marginBottom: 8
                            }} />
                            <div style={{ 
                              fontSize: 10, 
                              color: ACCENT_GRAY,
                              ...bodyFont
                            }}>
                              {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </div>
                            <div style={{ 
                              fontSize: 10, 
                              color: NEUTRAL_TEXT,
                              fontWeight: 600,
                              ...bodyFont
                            }}>
                              ${day.revenue}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      height: 120,
                      color: ACCENT_GRAY,
                      fontSize: 14,
                      ...bodyFont
                    }}>
                      No revenue data available for the last 7 days
                    </div>
                  )}
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 24 }}>
                {/* Remove Add Product button and add Close Shop */}
                <button
                  onClick={() => setShowCloseShopModal(true)}
                  style={{
                    background: 'none',
                    color: ERROR_RED,
                    border: 'none',
                    fontSize: 16,
                    fontWeight: 700,
                    textDecoration: 'underline',
                    minWidth: 44,
                    minHeight: 44,
                    fontFamily: 'Montserrat, sans-serif',
                    cursor: 'pointer',
                    boxShadow: 'none',
                  }}
                >
                  Close Shop
                </button>
              </div>

              {/* Step 1: Initial Confirmation Modal */}
              {showCloseShopModal && (
                <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: '#0008', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <div style={{ background: '#fff', borderRadius: 16, padding: 32, maxWidth: 420, width: '100%', boxShadow: '0 4px 24px #0002', display: 'flex', flexDirection: 'column', gap: 18 }}>
                    <h2 style={{ ...headingFont, fontSize: 22, color: ERROR_RED, margin: 0 }}>Are you sure you want to close your shop?</h2>
                    <div style={{ color: ACCENT_GRAY, fontSize: 15, ...bodyFont }}>
                      This action is permanent and cannot be undone. All of your shop details, products, and sales history will be permanently deleted. Are you absolutely sure you want to proceed?
                    </div>
                    <div style={{ display: 'flex', gap: 12, marginTop: 12 }}>
                      <button onClick={() => setShowCloseShopModal(false)} style={{ flex: 1, background: NEUTRAL_WHITE, color: PRIMARY_BROWN, border: `2px solid ${PRIMARY_BROWN}`, borderRadius: 10, padding: '12px 0', fontSize: 16, fontWeight: 700, cursor: 'pointer', ...bodyFont }}>Go Back</button>
                      <button onClick={handleCloseShop} style={{ flex: 1, background: ERROR_RED, color: '#fff', border: 'none', borderRadius: 10, padding: '12px 0', fontSize: 16, fontWeight: 700, cursor: 'pointer', ...bodyFont }}>Continue</button>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2: Final Confirmation Modal */}
              {showFinalConfirm && (
                <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: '#0008', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <div style={{ background: '#fff', borderRadius: 16, padding: 32, maxWidth: 420, width: '100%', boxShadow: '0 4px 24px #0002', display: 'flex', flexDirection: 'column', gap: 18 }}>
                    <h2 style={{ ...headingFont, fontSize: 20, color: ERROR_RED, margin: 0 }}>Type DELETE to confirm</h2>
                    <div style={{ color: ACCENT_GRAY, fontSize: 15, ...bodyFont }}>
                      To permanently delete your shop, please type the word <b>DELETE</b> in the box below.
                    </div>
                    <input
                      type="text"
                      value={deleteInput}
                      onChange={e => setDeleteInput(e.target.value)}
                      style={{ padding: 10, borderRadius: 8, border: '1px solid #E0E0E0', fontSize: 16, ...bodyFont }}
                      placeholder="Type DELETE to confirm"
                      autoFocus
                    />
                    {error && <div style={{ color: ERROR_RED, fontSize: 14 }}>{error}</div>}
                    <div style={{ display: 'flex', gap: 12, marginTop: 12 }}>
                      <button onClick={() => { setShowFinalConfirm(false); setDeleteInput(''); }} style={{ flex: 1, background: NEUTRAL_WHITE, color: PRIMARY_BROWN, border: `2px solid ${PRIMARY_BROWN}`, borderRadius: 10, padding: '12px 0', fontSize: 16, fontWeight: 700, cursor: 'pointer', ...bodyFont }}>Go Back</button>
                      <button
                        onClick={handleFinalDelete}
                        disabled={deleteInput !== 'DELETE' || deleting}
                        style={{ flex: 1, background: ERROR_RED, color: '#fff', border: 'none', borderRadius: 10, padding: '12px 0', fontSize: 16, fontWeight: 700, cursor: deleteInput === 'DELETE' && !deleting ? 'pointer' : 'not-allowed', opacity: deleteInput === 'DELETE' && !deleting ? 1 : 0.7, ...bodyFont }}
                      >
                        {deleting ? 'Deleting...' : 'Permanently Delete Shop'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          </div>
          )}
      </>


      <BottomNavigation />
      {/* Floating Checklist Widget - Show if onboarding is not completed */}
      {!onboardingCompleted && (
        <ChecklistWidget setupFeeCompleted={setupFeeCompleted} />
      )}
    </div>
  );
}

// Optimize with React.memo to prevent unnecessary re-renders
export const SellerHub = React.memo(SellerHubComponent); 